import React, { useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { forDesktop, isDesktop } from '@westwing/ui-kit/breakPoints';
import { textLinesLimitClipCss, header5, subtitle1, link, header6 } from '@westwing/ui-kit/typography';
import { CrossIcon } from '@westwing/ui-kit/Icons/StyledIcons';
import TickIcon from '@westwing/ui-kit/Icons/Svgs/TickIcon';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import useBellaTranslator from 'Client/hooks/useTranslator';
import { useBellaSelector } from 'Client/redux/hooks';
import useConfig from 'AppShell/hooks/useConfig';
import { ensureEndingSlash } from 'Client/urlService';
import useFeatureToggle from 'Client/hooks/useFeatureToggle';
import AddOnProductTile from 'Client/components/ProductDetailPage/Overlays/AddOn/AddOnProductTile';
import AddOnProductTileSkeleton from 'Client/components/ProductDetailPage/Overlays/AddOn/AddOnProductTileSkeleton';
import paths from 'Common/routes/paths';
import useGa from 'Client/hooks/useGa';
import { removeNotification } from 'Client/redux/notifications/actions';
import Notifications from 'Client/components/Notifications/Notifications';
import StateInterface from 'Client/redux/types';
import { NotificationMessage } from 'Client/redux/notifications/types';
import { BellaTestIds } from 'Common/qaIds';
import PriceDisclaimer from 'Client/components/Generic/PriceDisclaimer';
import { BinaryWithOffVariant } from 'AppShell/appshell_types';
import { AnimatedOverlayHeadline } from '@westwing/ui-kit/AnimatedOverlay';
import { useSegmentProductListViewedTracking } from 'Common/segment/useSegmentProductListViewedTracking';
import { SEGMENT_TRACKING_SOURCES } from 'Common/segment/types';
import useListingPageUrlState from 'Client/components/ListingPage/hooks/useListingPageUrlState';
import { useSegmentTracking } from 'Common/segment';
import { getSegmentProductPayload } from 'Client/components/ListingPage';

const HeaderWrapper = styled.div`
    ${textLinesLimitClipCss(2)}
    ${header5}
    display:none;
    ${forDesktop`
        display:initial;
    `}
`;

const HeaderContainer = styled.div`
    background: ${({ theme }) => theme.colors.green10};
    transition: background-color 0.3s;
    padding: 0 20px 20px;
    ${forDesktop`
        padding: 0 60px 32px;
    `}
`;

const HeadLine = styled.div`
    transition: background-color 0.3s;
    background-color: ${({ theme }) => theme.colors.green10};
`;

const AddedToCartWrapper = styled.div`
    ${subtitle1}
    display: flex;
    align-items: center;
    margin-bottom: 6px;
    color: ${({ theme }) => theme.colors.charcoal100};
    ${forDesktop`
        ${subtitle1}
        margin-top: 10px;
        margin-bottom: 20px;
    `}
`;

const StyledLink = styled(Link)`
    ${link}
    height: 21px;
    cursor: pointer;
    display: inline-block;
    &:visited {
        color: ${({ theme }) => theme.colors.turquoise100};
    }
`;

const FooterBackToCartWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 28px 0 30px;

    ${forDesktop`
        padding: 28px 0 50px;
    `}
`;

const FooterBtn = styled.button`
    ${link}
    color: ${({ theme }) => theme.colors.charcoal100};
    cursor: pointer;
`;

const StyledTickIcon = styled(TickIcon)`
    display: flex;
    width: 19px;
    height: 19px;
    margin-right: 12px;
    border-radius: 10px;
    color: ${({ theme }) => theme.colors.white};
    background: ${({ theme }) => theme.colors.iconSuccess};
`;

const ProductsWrapper = styled.div`
    flex-basis: 100%;
    padding: 25px 20px 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    ${forDesktop`
        padding: 40px 60px 0;
        margin: 0;
    `}
`;

const AddOnProductsTitle = styled.div`
    ${header6}
    padding-bottom: 4px;
    ${forDesktop`
    padding-bottom: 20px;
    `}
`;

const StyledNotifications = styled(Notifications)`
    position: sticky;
`;

const StyledCloseButtonBlock = styled(AnimatedOverlayHeadline)`
    padding: 16px;
`;

const StyledPriceDisclaimer = styled(PriceDisclaimer)`
    color: ${({ theme }) => theme.colors.charcoal50};
    font-weight: 390;
    line-height: 19px;
    letter-spacing: 0.35px;
    margin: 0 20px;
    padding: 0;
    padding-bottom: 30px;

    ${forDesktop`
        margin: 0 60px;
        padding-bottom 50px;
    `}
`;

type Props = {
    isOpen?: boolean;
    dismissFunction: () => void;
};

const AddOnNotifications = () => {
    const dispatch = useDispatch();
    const messages = useSelector<StateInterface, NotificationMessage[]>(state => state.notifications.messages).filter(
        message => message.isAddOnOverlay
    );

    const removeMessage = useCallback(
        (messageId: string) => {
            dispatch(removeNotification(messageId));
        },
        [dispatch]
    );

    return (
        <StyledNotifications
            messages={messages}
            dismissMessage={removeMessage}
            position="bottom"
            data-testid="add-on-notification-area"
        />
    );
};

const Header = () => {
    const productName = useBellaSelector(state => state.productdetails.productdetail.name);
    return <HeaderWrapper data-testid={BellaTestIds.pdpAddOnOverlayHeader}>{productName}</HeaderWrapper>;
};

const AddedToCart = () => {
    const t = useBellaTranslator();
    return (
        <AddedToCartWrapper>
            <StyledTickIcon width={30} height={19} /> {t('Product added to cart')}
        </AddedToCartWrapper>
    );
};

const AddOnContent = ({ isOpen = false, dismissFunction }: Props) => {
    const t = useBellaTranslator();
    const trackListingPageView = useSegmentProductListViewedTracking();
    const headerContainerRef = useRef<HTMLDivElement>(null);
    const addOnProducts = useBellaSelector(state => state.productdetails.addOnProducts.products);
    const hasDiscountedProductOnPage = useCallback(
        () => addOnProducts.some(product => product.numericPrice < product.numericOriginalPrice),
        [addOnProducts]
    );
    const [urlState] = useListingPageUrlState();
    const isLoading = useBellaSelector(state => state.productdetails.addOnProducts.isFetching);
    const isFetchingComplete = useBellaSelector(state => state.productdetails.addOnProducts.isFetchingComplete);
    const experiment = useBellaSelector(state => state.productdetails.addOnProducts.experiment);
    const journeyId = useBellaSelector(state => state.appShellLogin.journeyId);
    const activeSimpleSKU = useBellaSelector(state => state.productdetails.productdetail.activeSimpleSKU);

    const gaTracking = useGa();
    const segmentTracking = useSegmentTracking();
    const {
        links: { cart },
    } = useConfig();
    const { shopBellaCart, p100EnableNewOmnibusDirective } = useFeatureToggle();
    const cartUrl = shopBellaCart ? ensureEndingSlash(paths.CART) : cart;
    const skeletonProducts = new Array(!isDesktop() ? 3 : 4).fill(null).map((_, i) => i);

    useEffect(() => {
        if (isOpen) {
            gaTracking.trackAddonOverlay({ action: 'Overlay Impression', label: activeSimpleSKU });
            trackListingPageView({
                listId: SEGMENT_TRACKING_SOURCES.CROSS_SALE_RECOMMENDATION_OVERLAY,
                listLabel: urlState.listingPageKey,
                products: addOnProducts.map(getSegmentProductPayload),
            });
        }
    }, [isOpen, gaTracking, activeSimpleSKU]);

    useEffect(() => {
        if (isFetchingComplete && experiment.group !== BinaryWithOffVariant.off) {
            gaTracking.trackVariation(experiment.name, experiment.group);
            segmentTracking.trackExperimentViewed({
                experiment_name: `Cross sale ${experiment.name}`,
                variation_name: `${experiment.group} ${journeyId}`,
            });
        }
    }, [gaTracking, segmentTracking, activeSimpleSKU, isFetchingComplete, experiment, journeyId]);

    const footerBackToCartTrackingHandler = () => {
        gaTracking.trackAddonOverlay({ action: 'Zum Warenkorb - Click', label: 'Bottom' });
    };
    const backToCartTrackingHandler = () => {
        gaTracking.trackAddonOverlay({ action: 'Zum Warenkorb - Click', label: 'Top' });
    };

    return (
        <>
            <HeadLine>
                <StyledCloseButtonBlock>
                    <button
                        type="button"
                        onClick={dismissFunction}
                        data-testid={BellaTestIds.pdpAddOnOverlayClose}
                        aria-label={t('Close')}
                    >
                        <CrossIcon width={16} height={16} strokeWidth={5} />
                    </button>
                </StyledCloseButtonBlock>
                <HeaderContainer ref={headerContainerRef}>
                    <Header />
                    <AddedToCart />
                    <StyledLink onClick={backToCartTrackingHandler} data-testid="to-cart-btn-header" to={cartUrl}>
                        {t('Zum Warenkorb')}
                    </StyledLink>
                </HeaderContainer>
            </HeadLine>
            <ProductsWrapper>
                <div>
                    <AddOnProductsTitle>{t('Bestseller products of today')}</AddOnProductsTitle>
                    {isLoading
                        ? skeletonProducts.map(key => <AddOnProductTileSkeleton key={key} />)
                        : addOnProducts.map((product, index) => (
                              <AddOnProductTile
                                  data-testid="add-on-product-tile"
                                  product={product}
                                  key={product.simpleSku}
                                  position={index}
                              />
                          ))}
                </div>
                <FooterBackToCartWrapper>
                    <FooterBtn onClick={dismissFunction}>{t('Continue shopping')}</FooterBtn>
                    <StyledLink onClick={footerBackToCartTrackingHandler} data-testid="to-cart-btn-footer" to={cartUrl}>
                        {t('Zum Warenkorb')}
                    </StyledLink>
                </FooterBackToCartWrapper>
            </ProductsWrapper>
            <AddOnNotifications />
            {!p100EnableNewOmnibusDirective && (
                <StyledPriceDisclaimer pageHasSpecialPrice={hasDiscountedProductOnPage()} />
            )}
        </>
    );
};

export default AddOnContent;
